import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";

const JAIPUR = [26.9124, 75.7873];
const pin = L.divIcon({ className: "", html: '<div class="ks-pin"></div>', iconSize: [34, 44], iconAnchor: [17, 40] });
const GOOD_M = 30;

const Recenter = ({ pos }) => {
  const map = useMap();
  useEffect(() => { map.setView(pos, Math.max(map.getZoom(), 16)); }, [pos[0], pos[1]]); // eslint-disable-line
  return null;
};

const Resize = () => {
  const map = useMap();
  useEffect(() => {
    const ts = [120, 400, 900].map((ms) => setTimeout(() => map.invalidateSize(), ms));
    return () => ts.forEach(clearTimeout);
  }, [map]);
  return null;
};

const ClickToSet = ({ onChange }) => {
  useMapEvents({ click: (e) => onChange(e.latlng.lat, e.latlng.lng) });
  return null;
};

const useGps = (onChange, t) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const run = useRef({});
  const stop = () => { const r = run.current; if (r.wid != null) navigator.geolocation.clearWatch(r.wid); clearTimeout(r.timer); run.current = {}; };
  useEffect(() => stop, []);
  const fail = (err) => {
    setBusy(false);
    setMsg(err?.code === 1
      ? t("Location permission is blocked. Allow location for this site in your browser settings, or drag the pin to your place.")
      : t("Couldn't get your location. Turn on GPS, or drag the pin to your place."));
  };
  const detect = () => {
    if (!navigator.geolocation || !window.isSecureContext) return setMsg(t("GPS is not available. Please drag the pin to your place."));
    stop();
    setBusy(true);
    setMsg(t("Finding your location…"));
    let best = null;
    const finish = () => {
      stop();
      if (best) { setBusy(false); setMsg(`${t("Location found")} (±${Math.round(best.coords.accuracy)} m). ${t("Drag the pin if it is not exact.")}`); return; }
      navigator.geolocation.getCurrentPosition((p) => { best = p; onChange(p.coords.latitude, p.coords.longitude); finish(); }, fail,
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 });
    };
    run.current.wid = navigator.geolocation.watchPosition((p) => {
      if (!best || p.coords.accuracy < best.coords.accuracy) {
        best = p;
        onChange(p.coords.latitude, p.coords.longitude);
        setMsg(`${t("Improving accuracy…")} ±${Math.round(p.coords.accuracy)} m`);
      }
      if (p.coords.accuracy <= GOOD_M) finish();
    }, (err) => (err.code === 1 ? (stop(), fail(err)) : finish()), { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
    run.current.timer = setTimeout(finish, 12000);
  };
  return { msg, busy, detect };
};

export const MapPicker = ({ lat, lng, onChange, autoDetect = false }) => {
  const { t } = useApp();
  const { msg, busy, detect } = useGps(onChange, t);
  useEffect(() => { if (autoDetect && lat == null) detect(); }, []); // eslint-disable-line
  const pos = [lat ?? JAIPUR[0], lng ?? JAIPUR[1]];

  return (
    <div className="space-y-2">
      <div className="relative isolate z-0 h-60 overflow-hidden rounded-2xl border border-[#D2E0D8]" data-testid="map-picker">
        <MapContainer center={pos} zoom={lat == null ? 12 : 16} className="h-full w-full" scrollWheelZoom={false}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Marker position={pos} icon={pin} draggable
            eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); onChange(ll.lat, ll.lng); } }} />
          <Recenter pos={pos} />
          <Resize />
          <ClickToSet onChange={onChange} />
        </MapContainer>
        <button type="button" onClick={detect} disabled={busy} data-testid="detect-location-btn"
          className="absolute bottom-3 right-3 z-[500] flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#0D3B2B] shadow-lg active:scale-95 transition-transform disabled:opacity-80">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} {t("Use my location")}
        </button>
      </div>
      <p className="text-xs text-[#4A6358]" data-testid="map-status">{msg || t("Drag the pin or tap the map to set the exact spot.")}</p>
    </div>
  );
};
